import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Alert,
} from "@mui/material";
import {
  Videocam,
  ChevronRight,
  FiberManualRecord,
  AccessTime,
  Check,
} from "@mui/icons-material";
import RecordRTC from "recordrtc";
import { AxiosService } from "../../../services/axios/axiosService";
import { useNavigate, useLocation } from "react-router-dom";
import Timer from "./Timer";
import InterviewFeedbackModal from "./InterviewFeedbackModal";
import { useInterviewChunkUploads } from "./useInterviewChunkUploads";
import InterviewBackdrop from "./components/InterviewBackdrop";
import AttemptWarningDialog from "./components/AttemptWarningDialog";
import {
  persistInterviewId,
  getPersistedId,
  clearPersistedId,
  saveProgress,
  loadProgress,
  clearProgress,
  loadSession,
  saveSession,
  clearSession,
  saveFeedbackState,
  loadFeedbackState,
  clearFeedbackState,
  addLog,
  getLogs,
  clearLogs,
  clearAllForInterview,
} from "./interviewLogs";

// ============================Attempt Tracker=====================================================
// Attempt 1  → normal recording, log QUESTION_RECORDING_STARTED
// Attempt 2  → show FIRST warning popup ("last attempt, next refresh will auto-advance")
// Attempt 3  → show SECOND (final) warning THEN auto-advance + upload last chunk
// Attempt 4+ → auto-advance silently (safety net, should not normally be reached)

const MAX_ATTEMPTS_PER_QUESTION = 3;
const WARN_AT_ATTEMPT           = 2;
const ATTEMPTS_PREFIX           = "interview_attempts_";

const loadAttempts = (interviewId) => {
  try {
    const raw = localStorage.getItem(`${ATTEMPTS_PREFIX}${interviewId}`);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const saveAttempts = (interviewId, attempts) => {
  try {
    localStorage.setItem(
      `${ATTEMPTS_PREFIX}${interviewId}`,
      JSON.stringify(attempts),
    );
  } catch (_) {}
};

const clearAttempts = (interviewId) => {
  try { localStorage.removeItem(`${ATTEMPTS_PREFIX}${interviewId}`); } catch (_) {}
};

// Returns the NEW count after incrementing
const incrementAttempt = (interviewId, questionNum) => {
  const attempts  = loadAttempts(interviewId);
  const newCount  = (attempts[questionNum] ?? 0) + 1;
  attempts[questionNum] = newCount;
  saveAttempts(interviewId, attempts);
  return newCount;
};

// ============================ProgressTracker=================================================
const ProgressTracker = React.memo(({ currentQuestion, totalQuestions }) => (
  <Paper
    sx={{ p: 2, borderRadius: 2, border: "1px solid rgba(148, 163, 184, 0.4)" }}
  >
    <Typography variant="body1" fontWeight={600} mb={2}>
      Questions:
    </Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 0.5 }}>
      {Array.from({ length: totalQuestions }, (_, i) => {
        const num         = i + 1;
        const isActive    = num === currentQuestion;
        const isCompleted = num < currentQuestion;

        const borderColor   = isCompleted ? "#22c55e" : isActive ? "#3b82f6" : "#d4d7db";
        const textColor     = isCompleted ? "#166534" : isActive ? "#1d4ed8" : "#4b5563";
        const IconComponent = isCompleted ? Check : null;

        return (
          <Box
            key={num}
            sx={{
              position        : "relative",
              width           : 45,
              height          : 45,
              borderRadius    : 6,
              border          : `2px solid ${borderColor}`,
              backgroundColor : "#ffffff",
              display         : "flex",
              alignItems      : "center",
              justifyContent  : "center",
              fontSize        : 15,
              fontWeight      : 600,
              color           : textColor,
              boxSizing       : "border-box",
              ...(isActive && {
                animation: "questionPulse 1.6s ease-in-out infinite",
              }),
              "@keyframes questionPulse": {
                "0%"  : { boxShadow: "0 0 0 0 rgba(59,130,246,0.4)", transform: "translateY(0)" },
                "50%" : { boxShadow: "0 0 0 6px rgba(59,130,246,0)", transform: "translateY(-1px)" },
                "100%": { boxShadow: "0 0 0 0 rgba(59,130,246,0)",   transform: "translateY(0)" },
              },
            }}
          >
            {IconComponent && (
              <Box
                sx={{
                  position        : "absolute",
                  top             : -6,
                  right           : -6,
                  width           : 16,
                  height          : 16,
                  borderRadius    : 5,
                  border          : `1px solid ${borderColor}`,
                  backgroundColor : "#22c55e",
                  display         : "flex",
                  alignItems      : "center",
                  justifyContent  : "center",
                  boxShadow       : `0 0 0 2px ${borderColor}`,
                }}
              >
                <IconComponent sx={{ fontSize: 12, color: "#f9fafb" }} />
              </Box>
            )}
            {num}
          </Box>
        );
      })}
    </Box>
  </Paper>
));

// ============================CurrentQuestion=================================================
const CurrentQuestion = React.memo(
  ({ question, questionNumber, onNext, isSubmit, isNextEnabled, isLoading }) => (
    <Paper
      elevation={0}
      sx={{
        p            : 2,
        border       : "1px solid rgba(170, 168, 160, 0.3)",
        borderRadius : 2,
        height       : "100%",
        display      : "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} mb={1.5}>
        <Typography variant="body1" fontWeight={600} color="text.secondary">
          Question - {questionNumber}
        </Typography>
      </Stack>

      <Typography variant="h6" fontWeight={400} mb={2}>
        {question}
      </Typography>

      <Button
        variant="contained"
        size="medium"
        endIcon={isLoading || isSubmit ? null : <ChevronRight />}
        onClick={onNext}
        disabled={!isNextEnabled || isLoading}
        sx={{
          textTransform: "none",
          borderRadius : 1.5,
          mb           : 2,
          mt           : "auto",
          alignSelf    : "flex-start",
          px           : 2,
        }}
      >
        {isSubmit ? "Submit Interview" : "Next Question"}
      </Button>
    </Paper>
  ),
);

// ============================VideoPreview=================================================
const VideoPreview = React.memo(
  ({
    isLive,
    mediaStreamRef,
    audioStreamRef,
    onRecordingStart,
    timeRemaining,
    cameraError,
    onCameraError,
    onCameraGranted,
    onMicGranted,
    onBothGranted,
    preAnswerCountdown,
    suppressOverlays,
  }) => {
    const videoRef = useRef(null);

    useEffect(() => {
      let videoStream = null;
      let audioStream = null;

      const initCamera = async () => {
        try {
          // Step 1 — video + audio together
          videoStream = await navigator.mediaDevices.getUserMedia({
            video: {
              width     : { ideal: 640 },
              height    : { ideal: 360 },
              frameRate : { ideal: 15, max: 20 },
            },
            audio: { echoCancellation: true, noiseSuppression: true },
          });
          onCameraGranted();

          // Step 2 — dedicated audio stream for recorder
          audioStream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true },
          });
          onMicGranted();

          if (videoRef.current) {
            videoRef.current.srcObject = videoStream;
            mediaStreamRef.current     = videoStream;
            audioStreamRef.current     = audioStream;
          }

          onBothGranted();
          onRecordingStart(videoStream, audioStream);
        } catch (err) {
          onCameraError(err);
        }
      };

      initCamera();

      return () => {
        if (videoStream) videoStream.getTracks().forEach((t) => t.stop());
        if (audioStream) audioStream.getTracks().forEach((t) => t.stop());
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <Paper
        elevation={0}
        sx={{
          position      : "relative",
          height        : "100%",
          minHeight     : 400,
          background    : "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          border        : "1px solid rgba(255,255,255,0.53)",
          borderRadius  : 3,
          display       : "flex",
          alignItems    : "center",
          justifyContent: "center",
          overflow      : "hidden",
        }}
      >
        {isLive && !cameraError && !suppressOverlays && (
          <Chip
            icon={
              <FiberManualRecord
                sx={{ fontSize: 12, animation: "pulse 2s infinite" }}
              />
            }
            label="Recording"
            size="small"
            sx={{
              position      : "absolute",
              top           : 24,
              left          : 24,
              bgcolor       : "rgba(0,0,0,0.5)",
              backdropFilter: "blur(10px)",
              color         : "white",
              zIndex        : 10,
              "& .MuiChip-icon": { color: "#ef4444" },
            }}
          />
        )}

        {timeRemaining > 0 && !cameraError && !suppressOverlays && (
          <Box
            sx={{
              position      : "absolute",
              top           : 24,
              right         : 24,
              px            : 1.2,
              py            : 0.5,
              borderRadius  : 999,
              bgcolor       : timeRemaining <= 30
                ? "rgba(127,29,29,0.95)"
                : "rgba(15,23,42,0.8)",
              border        : timeRemaining <= 30
                ? "1px solid rgba(248,113,113,0.9)"
                : "1px solid rgba(148,163,184,0.6)",
              color         : "white",
              fontSize      : 15,
              fontWeight    : 600,
              zIndex        : 10,
              display       : "flex",
              alignItems    : "center",
              gap           : 0.75,
              boxShadow     : timeRemaining <= 30
                ? "0 0 0 1px rgba(248,113,113,0.6),0 0 12px rgba(220,38,38,0.8)"
                : "none",
              animation     : timeRemaining <= 30
                ? "timerPulse 1.5s ease-in-out infinite"
                : "none",
            }}
          >
            <AccessTime
              sx={{
                fontSize: 16,
                color   : timeRemaining <= 30 ? "#fecaca" : "#e5e7eb",
              }}
            />
            {timeRemaining <= 30 && (
              <Box component="span" sx={{ color: "#fecaca" }}>Auto submitting in</Box>
            )}
            <Box component="span">
              {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, "0")}
            </Box>
          </Box>
        )}

        {preAnswerCountdown > 0 && !cameraError && !suppressOverlays && (
          <Box
            sx={{
              position      : "absolute",
              inset         : 0,
              zIndex        : 12,
              display       : "flex",
              alignItems    : "center",
              justifyContent: "center",
              bgcolor       : "rgba(15, 23, 42, 0.42)",
              pointerEvents : "none",
            }}
          >
            <Box
              sx={{
                minWidth      : 240,
                px            : 3,
                py            : 2,
                borderRadius  : 2,
                textAlign     : "center",
                border        : "1px solid rgba(148, 163, 184, 0.6)",
                bgcolor       : "rgba(15,23,42,0.8)",
                backdropFilter: "blur(2px)",
                color         : "white",
                opacity       : 0.9,
              }}
            >
              <Typography variant="body2" sx={{ mb: 0.75 }}>
                You can start recording your answer in seconds:
              </Typography>
              <Typography variant="h2" fontWeight={700} lineHeight={1}>
                {preAnswerCountdown}
              </Typography>
            </Box>
          </Box>
        )}

        {cameraError ? (
          <Stack alignItems="center" spacing={2}>
            <Videocam sx={{ fontSize: 96, color: "rgba(255,255,255,0.2)" }} />
            <Typography variant="h6" color="rgba(255,255,255,0.4)">
              Camera / Microphone access denied
            </Typography>
            <Typography
              variant="body2"
              color="rgba(255,255,255,0.3)"
              textAlign="center"
              px={2}
            >
              {cameraError}
            </Typography>
            <Typography
              variant="body2"
              color="rgba(255,255,255,0.25)"
              textAlign="center"
              px={2}
            >
              Please enable camera and microphone permissions in your browser
              settings and refresh the page.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.location.reload()}
              sx={{
                color      : "rgba(255,255,255,0.6)",
                borderColor: "rgba(255,255,255,0.3)",
                textTransform: "none",
                "&:hover": { borderColor: "rgba(255,255,255,0.6)" },
              }}
            >
              Reload & Try Again
            </Button>
          </Stack>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}

        <style>{`
          @keyframes pulse      { 0%,100%{opacity:1}50%{opacity:0.5} }
          @keyframes timerPulse {
            0%,100%{transform:translateY(0);box-shadow:0 0 0 0 rgba(248,113,113,0.6)}
            50%    {transform:translateY(-1px);box-shadow:0 0 0 6px rgba(248,113,113,0)}
          }
        `}</style>
      </Paper>
    );
  },
);

// ============================Module-level fetch guard=================================================
let _hasFetched = false;

// ============================Main Interview Component=================================================
const Interview = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { token } = useParams();

  const interviewId = useMemo(() => {
    const fromState = location.state?.interviewId;
    if (fromState) {
      persistInterviewId(fromState);
      return fromState;
    }
    return getPersistedId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [interviewData,                    setInterviewData]                    = useState({});
  const [currentQuestion,                  setCurrentQuestion]                  = useState(1);
  const [isLive,                           setIsLive]                           = useState(false);
  const [isTtsCompletedForCurrentQuestion, setIsTtsCompletedForCurrentQuestion] = useState(false);
  const [isErr,                            setIsErr]                            = useState(null);
  const [audioUrl,                         setAudioUrl]                         = useState(null);
  const [isPreStartActive,                 setIsPreStartActive]                 = useState(false);
  const [preStartSeconds,                  setPreStartSeconds]                  = useState(0);
  const [timeRemaining,                    setTimeRemaining]                    = useState(0);
  const [isSubmitting,                     setIsSubmitting]                     = useState(false);
  const [isFinalizing,                     setIsFinalizing]                     = useState(false);
  const [isCompletionPending,              setIsCompletionPending]              = useState(false);
  const [showCompletionOverlay,            setShowCompletionOverlay]            = useState(false);
  const [isFeedbackOpen,                   setIsFeedbackOpen]                   = useState(false);
  const [feedbackStarted,                  setFeedbackStarted]                  = useState(false);
  const [feedbackStep,                     setFeedbackStep]                     = useState(0);
  const [feedbackAnswers,                  setFeedbackAnswers]                  = useState(() => ({}));
  const [finalUploadError,                 setFinalUploadError]                 = useState(null);
  const [isSendingLogs,                    setIsSendingLogs]                    = useState(false);
  const [cameraError,                      setCameraError]                      = useState(null);
  const [preAnswerCountdown,               setPreAnswerCountdown]               = useState(0);
  const [permissionResolved,               setPermissionResolved]               = useState(false);

  // Warning popup state
  const [showReRecordWarning, setShowReRecordWarning] = useState(false);
  const [showFinalWarning,    setShowFinalWarning]    = useState(false);

  const cameraErrorRef                = useRef(null);
  const isFeedbackOpenRef             = useRef(false);
  const reRecordWarningQNum           = useRef(null);
  const pendingAutoAdvanceRef         = useRef(null);
  const pageReloadLoggedRef           = useRef(false);
  const suppressTabLogsUntilRef       = useRef(0);
  const feedbackRestoreInProgressRef  = useRef(false);
  const completionFromFeedbackRef     = useRef(false);
  const isResumingRef                 = useRef(false);
  const mediaRecorderRef              = useRef(null);
  const audioRecorderRef              = useRef(null);
  const mediaStreamRef                = useRef(null);
  const audioStreamRef                = useRef(null);
  const audioChunksRef                = useRef([]);
  const audioPlayerRef                = useRef(null);
  const timerRef                      = useRef(null);
  const startAnswerTimerRef           = useRef(() => { timerRef.current?.start?.(); });
  const startRecordersForQuestionRef  = useRef(() => {});
  const preAnswerIntervalRef          = useRef(null);
  const hasPlayedFirstTtsRef          = useRef(false);
  const interviewDataRef              = useRef({});
  const currentQuestionRef            = useRef(1);
  const permissionReadyRef            = useRef(false);
  const pendingRecordingStartedByQRef = useRef(new Set());
  const recordingStartedLoggedByQRef  = useRef(new Set());
  const tabHiddenAtRef                = useRef(null);
  const tabVisibilityLoggedRef        = useRef(false);
  const completePromiseRef            = useRef(null);
  const completionReadyRef            = useRef(false);
  const completionInProgressRef       = useRef(false);
  const isAdvancingRef                = useRef(false);

  const FEEDBACK_QUESTIONS = useMemo(
    () => ([
      {
        id   : "overall",
        kind : "stars",
        text : "How would you rate your overall interview experience?",
        scale: [
          { value: 5, label: "Excellent" },
          { value: 4, label: "Good" },
          { value: 3, label: "Average" },
          { value: 2, label: "Poor" },
          { value: 1, label: "Very Poor" },
        ],
      },
      {
        id     : "clarity",
        kind   : "options",
        text   : "How clear and understandable were the interview questions?",
        options: ["Very Clear", "Mostly Clear", "Somewhat Clear", "Confusing", "Very Confusing"],
      },
      {
        id     : "difficulty",
        kind   : "options",
        text   : "How would you rate the difficulty level of the interview?",
        options: ["Very Easy", "Easy", "Moderate", "Difficult", "Very Difficult"],
      },
      {
        id     : "platform",
        kind   : "options",
        text   : "How was your experience with the interview platform (recording, navigation, etc.)?",
        options: ["Very Smooth", "Smooth", "Neutral", "Some Issues", "Major Issues"],
      },
      {
        id     : "instructions",
        kind   : "options",
        text   : "How clear were the instructions provided before/during the interview?",
        options: ["Very Clear", "Clear", "Average", "Unclear", "Very Unclear"],
      },
      {
        id     : "timing",
        kind   : "options",
        text   : "Was the time given for each question sufficient?",
        options: [
          "More than sufficient",
          "Sufficient",
          "Neutral",
          "Slightly insufficient",
          "Not sufficient at all",
        ],
      },
      {
        id     : "technical",
        kind   : "options",
        text   : "Did you face any technical issues during the interview?",
        options: [
          "No Issues",
          "Minor Issues (did not affect much)",
          "Moderate Issues (some disruption)",
          "Major Issues (significantly affected experience)",
        ],
      },
    ]),
    [],
  );

  useEffect(() => { interviewDataRef.current  = interviewData;  }, [interviewData]);
  useEffect(() => { currentQuestionRef.current = currentQuestion; }, [currentQuestion]);
  useEffect(() => { cameraErrorRef.current     = cameraError;    }, [cameraError]);
  useEffect(() => { isFeedbackOpenRef.current  = !!isFeedbackOpen; }, [isFeedbackOpen]);

  // If camera/mic is blocked, dismiss any re-record popups
  useEffect(() => {
    if (!cameraError) return;
    setShowReRecordWarning(false);
    setShowFinalWarning(false);
  }, [cameraError]);

  // ── logging helper ────────────────────────────────────────────────────────

  const addLogWithQuestionText = useCallback(
    (event, question = null, extra = {}) => {
      const qNum =
        typeof question === "number"
          ? question
          : typeof currentQuestionRef.current === "number"
            ? currentQuestionRef.current
            : null;

      const questionText =
        qNum && qNum > 0
          ? interviewDataRef.current?.questions?.[qNum - 1]?.questionText
          : undefined;

      addLog(interviewId, event, qNum, {
        questionText: questionText ?? null,
        ...extra,
      });
    },
    [interviewId],
  );

  // ── chunk upload circuit breaker ─────────────────────────────────────────

  const chunkFatalStatuses       = useMemo(() => new Set([404, 405, 501]), []);
  const chunkUploadsDisabledRef  = useRef(false);
  const chunkConsecutiveFatalRef = useRef(0);

  const noteChunkResponse = useCallback(
    (status, ok) => {
      if (ok) { chunkConsecutiveFatalRef.current = 0; return; }
      if (chunkFatalStatuses.has(status)) {
        chunkConsecutiveFatalRef.current += 1;
        if (
          chunkConsecutiveFatalRef.current >= 3 &&
          !chunkUploadsDisabledRef.current
        ) {
          chunkUploadsDisabledRef.current = true;
          console.warn(
            "[Chunk] Disabling further chunk uploads for this session " +
            `(consecutive ${status} from /upload-chunk-videoOnly).`,
          );
        }
      }
    },
    [chunkFatalStatuses],
  );

  // ── uploadChunk: POST /api/interview/upload-chunk ─────────────────────────
  // Payload: interviewId, questionId, uploadSessionId, chunkIndex, chunk,
  //          isLastChunk, responseDuration (slice per chunk; full total when isLastChunk)

  const uploadChunk = useCallback(
    async ({
      interviewId: iid,
      questionId,
      uploadSessionId,
      chunkIndex,
      audioBlob,
      videoBlob,
      isLastChunk,
      responseDurationSeconds = 0,
    }) => {
      if (!questionId) return false;
      if (!uploadSessionId) {
        console.warn(`[Chunk] upload-chunk #${chunkIndex} skipped — missing uploadSessionId`);
        return false;
      }
      if (chunkUploadsDisabledRef.current) return false;
      const MIN_VIDEO_BYTES = 256;

      const videoSize =
        videoBlob && typeof videoBlob.size === "number" ? videoBlob.size : 0;
      if (videoSize < MIN_VIDEO_BYTES) {
        
        return false;
      }
      const rawDur = Number(responseDurationSeconds);
      const durationSec =
        Number.isFinite(rawDur) && rawDur >= 0 ? Math.round(rawDur) : 0;
      const videoType =
        (videoBlob && typeof videoBlob.type === "string" && videoBlob.type) ||
        "video/webm";
      const videoExt = /mp4/i.test(videoType) ? "mp4" : "webm";

      const formData = new FormData();
      formData.append("interviewId",       iid ?? interviewId);
      formData.append("questionId",        questionId);
      formData.append("uploadSessionId",   uploadSessionId);
      formData.append("chunkIndex",        String(chunkIndex));
      formData.append("isLastChunk",      String(!!isLastChunk));
      formData.append("responseDuration", String(durationSec));
      const videoFile = new File(
        [videoBlob],
        `video_${chunkIndex}.${videoExt}`,
        { type: videoType },
      );
      formData.append("chunk", videoFile);

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/interview/upload-single-chunk`,
          { method: "POST", body: formData },
        );
        noteChunkResponse(res.status, res.ok);

        if (!res.ok) {
          try {
            const errBody = await res.json();
            console.warn(
              `[Chunk] upload-chunk #${chunkIndex} failed (${res.status}):`,
              errBody?.error || errBody,
            );
          } catch (_) {}
          return false;
        }
        try { await res.json(); } catch (_) {}
        return true;
      } catch (_) {
        return false;
      }
    },
    [interviewId, noteChunkResponse],
  );

  // ── chunk upload hook ─────────────────────────────────────────────────────
  // NOTE: finalizeResponse is intentionally NOT passed — /response is not used.
  //       isLastChunk=true on /upload-chunk is the only completion signal.

  const {
    startTrackedUpload,
    ensureAllUploadsSettled,
    areAllUploadsSuccessful,
    beginQuestionChunkSession,
    pushChunk,
    startChunkFlushTimer,
    markAnswerTimerStarted,
    getQuestionAnswerElapsedSeconds,
    endAnswerRecording,
    markCompletedCancelRetries,
  } = useInterviewChunkUploads({
    uploadChunk,
    interviewDataRef,
  });

  // ── answer timer start (also kicks off the 10-s chunk flush timer) ────────

  const startAnswerTimer = useCallback(() => {
    const qNum =
      typeof currentQuestionRef.current === "number"
        ? currentQuestionRef.current
        : null;
    if (typeof qNum === "number" && qNum > 0) {
      try { startRecordersForQuestionRef.current?.(qNum); } catch (_) {}
      try { startChunkFlushTimer(qNum); } catch (_) {}
    }
    timerRef.current?.start?.();
  }, [startChunkFlushTimer, markAnswerTimerStarted]);
  startAnswerTimerRef.current = startAnswerTimer;

  // ── 3-second pre-answer countdown ─────────────────────────────────────────

  const startPreAnswerCountdown = useCallback(() => {
    if (preAnswerIntervalRef.current) {
      clearInterval(preAnswerIntervalRef.current);
      preAnswerIntervalRef.current = null;
    }

    let remaining = 3;
    setIsTtsCompletedForCurrentQuestion(false);
    setPreAnswerCountdown(remaining);

    preAnswerIntervalRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(preAnswerIntervalRef.current);
        preAnswerIntervalRef.current = null;
        setPreAnswerCountdown(0);
        startAnswerTimerRef.current?.();
        setIsTtsCompletedForCurrentQuestion(true);
        return;
      }
      setPreAnswerCountdown(remaining);
    }, 1_000);
  }, []);

  // ── freeze UI for final submission ────────────────────────────────────────

  const freezeForFinalSubmission = useCallback(() => {
    timerRef.current?.pause?.();
    setIsLive(false);
    if (preAnswerIntervalRef.current) {
      clearInterval(preAnswerIntervalRef.current);
      preAnswerIntervalRef.current = null;
    }
    setPreAnswerCountdown(0);
    try {
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.src = "";
      }
    } catch (_) {}
  }, []);

  // ── TTS audio playback ────────────────────────────────────────────────────

  const playAudio = useCallback(
    (audioData) => {
      try {
        if (isFeedbackOpenRef.current) return;
        let url;
        if (audioData instanceof Blob) {
          url = URL.createObjectURL(audioData);
        } else if (typeof audioData === "string") {
          if (
            audioData.startsWith("http") ||
            audioData.startsWith("blob:") ||
            audioData.startsWith("data:")
          ) {
            url = audioData;
          } else {
            try {
              const bytes = atob(audioData);
              const arr   = new Uint8Array(bytes.length);
              for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
              url = URL.createObjectURL(new Blob([arr], { type: "audio/mpeg" }));
            } catch { url = audioData; }
          }
        } else {
          url = audioData;
        }

        setAudioUrl(url);
        const el = audioPlayerRef.current;
        if (!el) return;
        el.src = url;
        el.load();
        el.onended = () => {
          if (isFeedbackOpenRef.current) return;
          startPreAnswerCountdown();
        };
        el.play()?.catch(() => {
          if (isFeedbackOpenRef.current) return;
          startPreAnswerCountdown();
        });
      } catch {
        if (isFeedbackOpenRef.current) return;
        startPreAnswerCountdown();
      }
    },
    [startPreAnswerCountdown],
  );

  // ── TTS fetch ─────────────────────────────────────────────────────────────

  const fetchAudioScript = useCallback(
    async (questionText) => {
      if (cameraErrorRef.current) return;
      if (isFeedbackOpenRef.current) return;
      if (!questionText?.trim()) {
        startAnswerTimerRef.current?.();
        setIsTtsCompletedForCurrentQuestion(true);
        return;
      }
      try {
        const response = await AxiosService.post(
          "/api/Interview/text-to-speech",
          { Text: questionText },
          { responseType: "blob" },
        );
        if (response) {
          if (cameraErrorRef.current) return;
          if (isFeedbackOpenRef.current) return;
          playAudio(response);
        } else {
          startAnswerTimerRef.current?.();
          setIsTtsCompletedForCurrentQuestion(true);
        }
      } catch {
        startAnswerTimerRef.current?.();
        setIsTtsCompletedForCurrentQuestion(true);
      }
    },
    [playAudio],
  );

  // ── start recorders for a question ───────────────────────────────────────

  const startRecordersForQuestion = useCallback(
    (questionNum) => {
      if (!mediaStreamRef.current || !audioStreamRef.current) return;

      // Stop any previously-running recorders before creating new ones.
      // (Prevents duplicate recorder pairs emitting double chunks.)
      try {
        const prevVideo = mediaRecorderRef.current;
        if (prevVideo) {
          if (typeof prevVideo.stopRecording === "function") {
            prevVideo.stopRecording(() => {});
          } else if (typeof prevVideo.stop === "function") {
            prevVideo.stop();
          }
        }
      } catch (_) {}
      try {
        const prevAudio = audioRecorderRef.current;
        if (prevAudio && typeof prevAudio.stop === "function" && prevAudio.state !== "inactive") {
          prevAudio.stop();
        }
      } catch (_) {}
      mediaRecorderRef.current = null;
      audioRecorderRef.current = null;

      // Persist progress immediately so reloads resume at the correct question
      try { saveProgress(interviewId, { currentQuestion: questionNum }); } catch (_) {}

      // Begin a per-question chunk session
      const questionId =
        interviewDataRef.current?.questions?.[questionNum - 1]?.id ?? null;
      try { beginQuestionChunkSession(questionNum, questionId); } catch (_) {}
      try { markAnswerTimerStarted(questionNum); } catch (_) {}

      const CHUNK_TIMESLICE_MS = 10_000;

      const ua        = typeof navigator !== "undefined" ? (navigator.userAgent || "") : "";
      const isSafari  = /safari/i.test(ua) && !/chrome|chromium|crios|edg/i.test(ua);

      const pickSupported = (candidates) => {
        try {
          if (!window.MediaRecorder || typeof MediaRecorder.isTypeSupported !== "function") return "";
          return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
        } catch { return ""; }
      };

      const safariVideoMime = pickSupported([
        "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
        "video/mp4",
      ]);

      const createRecordRtcVideo = () =>
        new RecordRTC(mediaStreamRef.current, {
          type         : "video",
          recorderType : RecordRTC.MediaStreamRecorder,
          mimeType     : "video/webm;codecs=vp8,opus",
          bitsPerSecond: 128_000,
          timeSlice    : CHUNK_TIMESLICE_MS,
          ondataavailable: (blob) => {
            try { pushChunk(questionNum, "video", blob); } catch (_) {}
          },
        });

      const createSafariVideoAdapter = () => {
        const chunks   = [];
        const mimeType = safariVideoMime || "";
        const opts     = mimeType ? { mimeType } : undefined;
        const mr       = new MediaRecorder(mediaStreamRef.current, opts);
        let blob       = new Blob([], { type: mimeType || mr.mimeType || "video/mp4" });
        mr.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            chunks.push(e.data);
            try { pushChunk(questionNum, "video", e.data); } catch (_) {}
          }
        };
        mr.onstop = () => {
          const t = mimeType || mr.mimeType || "video/mp4";
          blob    = new Blob(chunks, { type: t });
        };
        return {
          startRecording: ()      => mr.start(CHUNK_TIMESLICE_MS),
          stopRecording : (cb)    => {
            const done = () => cb?.();
            mr.addEventListener("stop", done, { once: true });
            try { mr.stop(); } catch { done(); }
          },
          getBlob       : ()      => blob,
        };
      };

      const videoRecorder =
        isSafari && window.MediaRecorder && safariVideoMime
          ? createSafariVideoAdapter()
          : createRecordRtcVideo();

      mediaRecorderRef.current = videoRecorder;

      const audioMime = pickSupported([
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/aac",
      ]);
      const audioRecorder = new MediaRecorder(
        audioStreamRef.current,
        audioMime ? { mimeType: audioMime } : undefined,
      );
      audioRecorderRef.current = audioRecorder;
      audioChunksRef.current   = [];

      audioRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
          try { pushChunk(questionNum, "audio", e.data); } catch (_) {}
        }
      };
      audioRecorder.onstop = () => {};

      videoRecorder.startRecording();
      audioRecorder.start(CHUNK_TIMESLICE_MS);
      setIsLive(true);

      if (
        permissionReadyRef.current &&
        !recordingStartedLoggedByQRef.current.has(questionNum)
      ) {
        recordingStartedLoggedByQRef.current.add(questionNum);
        pendingRecordingStartedByQRef.current.delete(questionNum);
        addLogWithQuestionText("QUESTION_RECORDING_STARTED", questionNum, {
          attemptNumber: 1,
        });
      } else if (!permissionReadyRef.current) {
        pendingRecordingStartedByQRef.current.add(questionNum);
      }
    },
    [
      interviewId,
      beginQuestionChunkSession,
      markAnswerTimerStarted,
      pushChunk,
      addLogWithQuestionText,
    ],
  );
  startRecordersForQuestionRef.current = startRecordersForQuestion;

  // ── permission callbacks ──────────────────────────────────────────────────

  const setPermissionState = useCallback(
    (patch) => {
      const existing = loadSession(interviewId) || {};
      saveSession(interviewId, {
        ...existing,
        permissionState: { ...(existing.permissionState || {}), ...patch },
      });
    },
    [interviewId],
  );

  const shouldLogPermissionGrant = useCallback(
    (kind) => {
      const s  = loadSession(interviewId) || {};
      const st = s.permissionState || {};
      if (st[`${kind}Revoked`])      return true;
      if (!st[`${kind}GrantedLogged`]) return true;
      return false;
    },
    [interviewId],
  );

  const handleCameraGranted = useCallback(() => {
    if (!shouldLogPermissionGrant("camera")) return;
    addLogWithQuestionText(
      "CAMERA_ACCESS_GRANTED",
      isFeedbackOpenRef.current ? 0 : null,
      {
        device: "camera",
        ...(isFeedbackOpenRef.current ? { questionText: "Feedback" } : {}),
      },
    );
    setPermissionState({ cameraGrantedLogged: true, cameraRevoked: false });
  }, [addLogWithQuestionText, setPermissionState, shouldLogPermissionGrant]);

  const handleMicGranted = useCallback(() => {
    if (!shouldLogPermissionGrant("mic")) return;
    addLogWithQuestionText(
      "MIC_ACCESS_GRANTED",
      isFeedbackOpenRef.current ? 0 : null,
      {
        device: "microphone",
        ...(isFeedbackOpenRef.current ? { questionText: "Feedback" } : {}),
      },
    );
    setPermissionState({ micGrantedLogged: true, micRevoked: false });
  }, [addLogWithQuestionText, setPermissionState, shouldLogPermissionGrant]);

  const handleBothGranted = useCallback(() => {
    permissionReadyRef.current = true;
    setPermissionResolved(true);
    suppressTabLogsUntilRef.current = Date.now() + 1_500;

    if (shouldLogPermissionGrant("both")) {
      addLogWithQuestionText(
        "CAMERA_MIC_ACCESS_GRANTED",
        isFeedbackOpenRef.current ? 0 : null,
        {
          device: "camera+microphone",
          ...(isFeedbackOpenRef.current ? { questionText: "Feedback" } : {}),
        },
      );
      setPermissionState({ bothGrantedLogged: true, bothRevoked: false });
    }

    const qNum =
      typeof currentQuestionRef.current === "number"
        ? currentQuestionRef.current
        : null;
    if (
      typeof qNum === "number" &&
      qNum > 0 &&
      pendingRecordingStartedByQRef.current.has(qNum) &&
      !recordingStartedLoggedByQRef.current.has(qNum)
    ) {
      pendingRecordingStartedByQRef.current.delete(qNum);
      recordingStartedLoggedByQRef.current.add(qNum);
      addLogWithQuestionText("QUESTION_RECORDING_STARTED", qNum, {
        attemptNumber: 1,
      });
    }
  }, [addLogWithQuestionText, setPermissionState, shouldLogPermissionGrant]);

  const handleRecordingStart = useCallback(
    (videoStream, audioStream) => {
      mediaStreamRef.current = videoStream;
      audioStreamRef.current = audioStream;

      try {
        const fb = loadFeedbackState(interviewId);
        if (isFeedbackOpen || (fb?.open && !fb?.completed)) {
          setIsLive(false);
          return;
        }
      } catch (_) {}

      // Streams only — recorders start when the answer timer begins (after TTS / countdown).
    },
    [interviewId, isFeedbackOpen],
  );

  const handleCameraError = useCallback(
    async (err) => {
      const errMessage =
        typeof err === "string" ? err : err?.message || "Camera/microphone error";
      setCameraError(errMessage);
      setPermissionResolved(true);
      suppressTabLogsUntilRef.current = Date.now() + 6_000;
      timerRef.current?.pause?.();
      if (preAnswerIntervalRef.current) {
        clearInterval(preAnswerIntervalRef.current);
        preAnswerIntervalRef.current = null;
      }
      setPreAnswerCountdown(0);
      setIsTtsCompletedForCurrentQuestion(false);
      try {
        if (audioPlayerRef.current) {
          audioPlayerRef.current.pause();
          audioPlayerRef.current.src = "";
        }
      } catch (_) {}

      try {
        const [cam, mic] = await Promise.all([
          navigator.permissions?.query?.({ name: "camera" }),
          navigator.permissions?.query?.({ name: "microphone" }),
        ]);
        const camDenied = cam?.state === "denied";
        const micDenied = mic?.state === "denied";
        if (camDenied && micDenied) {
          addLogWithQuestionText("CAMERA_MIC_ACCESS_DENIED", null, { error: errMessage });
        } else if (camDenied) {
          addLogWithQuestionText("CAMERA_ACCESS_DENIED", null, { device: "camera", error: errMessage });
        } else if (micDenied) {
          addLogWithQuestionText("MIC_ACCESS_DENIED", null, { device: "microphone", error: errMessage });
        } else {
          addLogWithQuestionText("CAMERA_MIC_ACCESS_DENIED", null, { error: errMessage });
        }
      } catch {
        addLogWithQuestionText("CAMERA_MIC_ACCESS_DENIED", null, { error: errMessage });
      }
    },
    [addLogWithQuestionText],
  );

  // ── send logs to backend ──────────────────────────────────────────────────

  const insertLogs = useCallback(
    async (logs) => {
      if (isSendingLogs) return;
      setIsSendingLogs(true);
      try {
        await AxiosService.post("/api/EventLogs/bulk", logs);
        clearLogs(interviewId);
      } catch (_) {
      } finally {
        setIsSendingLogs(false);
      }
    },
    [interviewId, isSendingLogs],
  );

  // ── complete interview (API + logs) ──────────────────────────────────────

  const completeInterviewApiOnly = useCallback(async () => {
    if (completionReadyRef.current)    return;
    if (completionInProgressRef.current) return;
    completionInProgressRef.current = true;

    try {
      const toSafeErrorString = (e) => {
        try {
          if (typeof e === "string") return e;
          const data = e?.response?.data;
          if (typeof data === "string") return data;
          if (data != null) return JSON.stringify(data);
          if (typeof e?.message === "string") return e.message;
          return JSON.stringify(e);
        } catch { return "Unknown error"; }
      };

      let completeOk = false;
      try {
        const res  = await AxiosService.post(`/api/Interview/${interviewId}/complete`);
        completeOk = res?.success === true;
      } catch (err) {
        addLogWithQuestionText("INTERVIEW_COMPLETION_FAILED", null, {
          error: toSafeErrorString(err),
        });
      }

      const logsBeforeCompletion = getLogs(interviewId);
      const treatAsFeedback =
        !!(isFeedbackOpenRef.current || completionFromFeedbackRef.current);

      addLogWithQuestionText(
        completeOk ? "INTERVIEW_COMPLETED" : "INTERVIEW_COMPLETION_FAILED",
        treatAsFeedback ? 0 : null,
        {
          totalLogs: logsBeforeCompletion.length,
          ...(treatAsFeedback ? { questionText: "Feedback" } : {}),
        },
      );

      const finalLogs = getLogs(interviewId);
      await insertLogs(finalLogs);

      completionReadyRef.current = true;
      setIsCompletionPending(false);
    } finally {
      completionInProgressRef.current = false;
      try { markCompletedCancelRetries?.(); } catch (_) {}
      clearLogs(interviewId);
      clearProgress(interviewId);
      clearSession(interviewId);
      clearAttempts(interviewId);
      clearPersistedId();
    }
  }, [addLogWithQuestionText, interviewId, insertLogs, markCompletedCancelRetries]);

  const completeInterviewAndRedirect = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (!completionReadyRef.current) {
        await completeInterviewApiOnly();
      }
    } finally {
      navigate(`/interview-completion/${token}`);
    }
  }, [completeInterviewApiOnly, isSubmitting, navigate, token]);

  // ── advance to next question ──────────────────────────────────────────────

  const advanceToNextQuestion = useCallback(
    async (fromQuestion) => {
      const data   = interviewDataRef.current;
      const totalQ = data?.questions?.length || 0;

      if (fromQuestion >= totalQ) {
        completeInterviewAndRedirect();
        return;
      }

      const nextQ = fromQuestion + 1;
      saveProgress(interviewId, { currentQuestion: nextQ });
      setCurrentQuestion(nextQ);
      setIsTtsCompletedForCurrentQuestion(false);
      setPreAnswerCountdown(0);
      audioChunksRef.current = [];

      const nextText = data?.questions?.[nextQ - 1]?.questionText;
      if (nextText) {
        await fetchAudioScript(nextText);
      } else {
        startAnswerTimerRef.current?.();
        setIsTtsCompletedForCurrentQuestion(true);
      }
      // Recorders restart in startAnswerTimer when the next answer period begins.
    },
    [interviewId, completeInterviewAndRedirect, fetchAudioScript],
  );

  // ── stop recorders when answer ends (timer, Next, max-attempts) ───────────

  const stopActiveRecorders = useCallback(
    async (qNum) => {
      timerRef.current?.pause?.();
      setIsLive(false);

      try {
        const audioRec = audioRecorderRef.current;
        if (audioRec && audioRec.state === "recording") {
          await new Promise((resolve) => {
            const originalOnStop = audioRec.onstop;
            audioRec.onstop = (e) => {
              if (originalOnStop) originalOnStop(e);
              resolve();
            };
            audioRec.stop();
          });
        }
      } catch (_) {}

      try {
        const videoRec = mediaRecorderRef.current;
        if (videoRec) {
          if (typeof videoRec.stopRecording === "function") {
            await new Promise((resolve) => {
              videoRec.stopRecording(() => resolve());
            });
          } else if (
            typeof videoRec.stop === "function" &&
            videoRec.state !== "inactive"
          ) {
            await new Promise((resolve) => {
              videoRec.addEventListener("stop", () => resolve(), { once: true });
              try { videoRec.stop(); } catch { resolve(); }
            });
          }
        }
      } catch (_) {}

      if (typeof qNum === "number" && qNum > 0) {
        try { endAnswerRecording(qNum); } catch (_) {}
      }

      mediaRecorderRef.current = null;
      audioRecorderRef.current = null;
    },
    [endAnswerRecording],
  );

  const capQuestionDurationSeconds = useCallback((qNum, elapsed) => {
    const expected =
      interviewDataRef.current?.questions?.[qNum - 1]?.expectedDurationSeconds;
    const sec =
      typeof elapsed === "number" && Number.isFinite(elapsed)
        ? Math.floor(elapsed)
        : 0;
    if (typeof expected === "number" && expected > 0) {
      return Math.min(sec, Math.round(expected));
    }
    return sec;
  }, []);

  // ── auto-advance: stop recorders → upload last chunk → advance ───────────
  // This is the single path used by Next button, time-up, and max-attempts.

  const autoAdvanceWithUpload = useCallback(
    async (qNum) => {
      const data   = interviewDataRef.current;
      const totalQ = data?.questions?.length || 0;

      await stopActiveRecorders(qNum);

      const durationSeconds = capQuestionDurationSeconds(
        qNum,
        getQuestionAnswerElapsedSeconds(qNum),
      );

      // ── Step 4: Last question → freeze + show feedback while uploading ──
      if (qNum >= totalQ) {
        freezeForFinalSubmission();
        setFinalUploadError(null);

        // Upload in background — feedback modal shows immediately
        const uploadPromise = startTrackedUpload({
          questionNumber : qNum,
          durationSeconds,
        });
        uploadPromise.catch((err) => {
          setFinalUploadError(err?.message || "Upload failed. Please try again.");
        });

        setFeedbackStep(0);
        setFeedbackAnswers({});
        setFeedbackStarted(false);
        setIsFeedbackOpen(true);
        return;
      }

      // ── Step 5: Non-last question → upload in background, advance ───────
      startTrackedUpload({
        questionNumber : qNum,
        durationSeconds,
      }).catch((err) =>
        console.error("[Interview] Background upload failed:", err),
      );

      await advanceToNextQuestion(qNum);
    },
    [
      advanceToNextQuestion,
      freezeForFinalSubmission,
      startTrackedUpload,
      stopActiveRecorders,
      capQuestionDurationSeconds,
      getQuestionAnswerElapsedSeconds,
    ],
  );

  // ── final-warning acknowledge ─────────────────────────────────────────────

  const handleFinalWarningAcknowledge = useCallback(() => {
    setShowFinalWarning(false);
    const fn = pendingAutoAdvanceRef.current;
    pendingAutoAdvanceRef.current = null;
    if (fn) fn();
  }, []);

  // ── fetch interview data ──────────────────────────────────────────────────

  const runFetchInterviewData = useCallback(async () => {
    if (!interviewId) {
      setIsErr("Interview session not found. Please go back and start again.");
      return;
    }

    try {
      const response = await AxiosService.post(`/api/Interview/${interviewId}/start`);
      setInterviewData(response);
      interviewDataRef.current = response;

      const totalQ = response?.questions?.length || 0;
      const saved  = loadProgress(interviewId);
      const savedQ = saved?.currentQuestion ?? 1;

      const navEntries    = performance.getEntriesByType("navigation");
      const isReload      = navEntries?.[0]?.type === "reload";
      const isBackForward = navEntries?.[0]?.type === "back_forward";

      // Resume from saved progress
      if (saved && savedQ >= 1 && savedQ <= totalQ) {
        const savedFeedback      = loadFeedbackState(interviewId);
        const feedbackInProgress = !!(savedFeedback?.open && !savedFeedback?.completed);

        if (isReload) {
          if (!pageReloadLoggedRef.current) {
            pageReloadLoggedRef.current = true;
            const existingReloads =
              getLogs(interviewId).filter((l) => l.event === "PAGE_RELOADED").length;
            addLogWithQuestionText(
              "PAGE_RELOADED",
              feedbackInProgress ? 0 : savedQ,
              {
                count          : existingReloads + 1,
                totalQuestions : totalQ,
                ...(feedbackInProgress ? { questionText: "Feedback" } : {}),
              },
            );
          }
        } else {
          const eventName = isBackForward ? "BACK_NAVIGATION_REOPENED" : "LINK_REOPENED";
          addLogWithQuestionText(
            eventName,
            feedbackInProgress ? 0 : savedQ,
            {
              action         : "resume_existing_progress",
              totalQuestions : totalQ,
              ...(feedbackInProgress ? { questionText: "Feedback" } : {}),
            },
          );
        }
        isResumingRef.current = true;
        setCurrentQuestion(savedQ);
        setIsPreStartActive(false);
        return;
      }

      // Stale progress — clear and fresh start
      if (saved && savedQ > totalQ) {
        clearAllForInterview(interviewId);
        clearAttempts(interviewId);
        addLogWithQuestionText("FRESH_START", 1, {
          reason: "stale_progress_cleared",
          totalQ,
        });
        setIsPreStartActive(true);
        setPreStartSeconds(5);
        return;
      }

      // Feedback in-progress without interview progress — restore feedback
      const savedFeedback = loadFeedbackState(interviewId);
      if (savedFeedback?.open && !savedFeedback?.completed) {
        const resumeQ =
          saved && savedQ >= 1 && savedQ <= totalQ
            ? savedQ
            : typeof totalQ === "number" && totalQ > 0
              ? totalQ
              : 1;

        if (isReload) {
          if (!pageReloadLoggedRef.current) {
            pageReloadLoggedRef.current = true;
            const existingReloads =
              getLogs(interviewId).filter((l) => l.event === "PAGE_RELOADED").length;
            addLogWithQuestionText("PAGE_RELOADED", 0, {
              count          : existingReloads + 1,
              totalQuestions : totalQ,
              questionText   : "Feedback",
            });
          }
        } else {
          const eventName = isBackForward ? "BACK_NAVIGATION_REOPENED" : "LINK_REOPENED";
          addLogWithQuestionText(eventName, 0, {
            action         : "resume_feedback_in_progress",
            totalQuestions : totalQ,
            questionText   : "Feedback",
          });
        }

        isResumingRef.current = true;
        setCurrentQuestion(resumeQ);
        setIsPreStartActive(false);
        feedbackRestoreInProgressRef.current = true;
        freezeForFinalSubmission();
        setFeedbackStarted(!!savedFeedback.started);
        setFeedbackStep(
          typeof savedFeedback.step === "number" ? savedFeedback.step : 0,
        );
        setFeedbackAnswers(
          savedFeedback.answers && typeof savedFeedback.answers === "object"
            ? savedFeedback.answers
            : {},
        );
        setIsFeedbackOpen(true);
        queueMicrotask(() => { feedbackRestoreInProgressRef.current = false; });
        return;
      }

      // Fresh start
      clearLogs(interviewId);
      clearAttempts(interviewId);
      const existingSession = loadSession(interviewId);
      const attemptCount    = (existingSession?.attemptCount ?? 0) + 1;
      saveSession(interviewId, {
        startedAt   : new Date().toISOString(),
        attemptCount,
        totalQuestion: totalQ,
        permissionState: {
          cameraGrantedLogged: false,
          micGrantedLogged   : false,
          bothGrantedLogged  : false,
          cameraRevoked      : false,
          micRevoked         : false,
          bothRevoked        : false,
        },
      });

      addLogWithQuestionText("FRESH_START", 1, { totalQ, attemptCount });
      setCurrentQuestion(1);
      setIsPreStartActive(true);
      setPreStartSeconds(5);
    } catch (err) {
      setIsErr(err?.message || "Failed to load interview. Please try again.");
    }
  }, [interviewId, addLogWithQuestionText, freezeForFinalSubmission]);

  const fetchInterviewData = useCallback(async () => {
    if (_hasFetched) return;
    _hasFetched = true;
    await runFetchInterviewData();
  }, [runFetchInterviewData]);

  // ── Next button handler ───────────────────────────────────────────────────

  const handleNextQuestion = useCallback(async () => {
    if (cameraError)    return;
    if (isFinalizing || isSubmitting || isFeedbackOpen) return;
    if (isAdvancingRef.current) return;

    const qNum =
      typeof currentQuestionRef.current === "number"
        ? currentQuestionRef.current
        : currentQuestion;
    if (typeof qNum !== "number" || qNum <= 0) return;

    isAdvancingRef.current = true;
    try {
      await autoAdvanceWithUpload(qNum);
    } finally {
      isAdvancingRef.current = false;
    }
  }, [
    cameraError,
    isFinalizing,
    isSubmitting,
    isFeedbackOpen,
    autoAdvanceWithUpload,
  ]);

  // ── feedback submit ───────────────────────────────────────────────────────

  const handleFeedbackSubmit = useCallback(async () => {
    setShowCompletionOverlay(true);
    completionFromFeedbackRef.current = true;
    try {
      if (!areAllUploadsSuccessful()) {
        await ensureAllUploadsSettled();
      }
      await completeInterviewApiOnly();
      setIsFeedbackOpen(false);
      clearFeedbackState(interviewId);
      navigate(`/interview-completion/${token}`);
    } finally {
      completionFromFeedbackRef.current = false;
      setShowCompletionOverlay(false);
    }
  }, [
    completeInterviewApiOnly,
    ensureAllUploadsSettled,
    areAllUploadsSuccessful,
    interviewId,
    navigate,
    token,
  ]);

  const waitForBackgroundCompletion = useCallback(async () => {
    if (completionReadyRef.current) return;
    setShowCompletionOverlay(true);
    try {
      await completePromiseRef.current;
    } finally {
      setShowCompletionOverlay(false);
    }
  }, []);

  const submitFeedbackAnswer = useCallback(async ({ interviewId: iid, question, answer }) => {
    await AxiosService.post("/api/Interview/feedback", {
      interview_id: iid,
      question,
      feedback    : answer,
    });
  }, []);

  const handleTimeUp = useCallback(() => {
    if (cameraError || isAdvancingRef.current) return;
    if (isFinalizing || isSubmitting || isFeedbackOpen) return;

    const qNum =
      typeof currentQuestionRef.current === "number"
        ? currentQuestionRef.current
        : null;
    if (typeof qNum !== "number" || qNum <= 0) return;

    timerRef.current?.pause?.();
    setIsLive(false);

    void (async () => {
      isAdvancingRef.current = true;
      try {
        await stopActiveRecorders(qNum);
        await autoAdvanceWithUpload(qNum);
      } finally {
        isAdvancingRef.current = false;
      }
    })();
  }, [
    cameraError,
    isFinalizing,
    isSubmitting,
    isFeedbackOpen,
    stopActiveRecorders,
    autoAdvanceWithUpload,
  ]);

  // ── derived values ────────────────────────────────────────────────────────

  const currentQuestionText = useMemo(
    () => interviewData?.questions?.[currentQuestion - 1]?.questionText || "",
    [currentQuestion, interviewData],
  );

  const currentQuestionDuration = useMemo(() => {
    const s = interviewData?.questions?.[currentQuestion - 1]?.expectedDurationSeconds;
    return typeof s === "number" && s > 0 ? s : 0;
  }, [currentQuestion, interviewData]);

  // ── effects ───────────────────────────────────────────────────────────────

  // Reset fetch guard on unmount so back-navigation works
  useEffect(() => { return () => { _hasFetched = false; }; }, []);

  useEffect(() => { fetchInterviewData(); }, [fetchInterviewData]);

  // Close feedback modal while the completion overlay is showing
  useEffect(() => {
    if (showCompletionOverlay) setIsFeedbackOpen(false);
  }, [showCompletionOverlay]);

  // Freeze timers/recorders when feedback is open
  useEffect(() => {
    if (!isFeedbackOpen) return;
    try { freezeForFinalSubmission(); } catch (_) {}
    try {
      if (audioRecorderRef.current?.state === "recording") {
        audioRecorderRef.current.stop();
      }
    } catch (_) {}
    try {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stopRecording?.();
      }
    } catch (_) {}
  }, [isFeedbackOpen, freezeForFinalSubmission]);

  // Persist feedback progress
  useEffect(() => {
    if (!interviewId)                          return;
    if (!isFeedbackOpen)                       return;
    if (feedbackRestoreInProgressRef.current)  return;
    saveFeedbackState(interviewId, {
      open     : true,
      started  : !!feedbackStarted,
      step     : typeof feedbackStep === "number" ? feedbackStep : 0,
      answers  : feedbackAnswers || {},
      completed: false,
      savedAt  : new Date().toISOString(),
    });
  }, [interviewId, isFeedbackOpen, feedbackAnswers, feedbackStarted, feedbackStep]);

  // Restore feedback on reload once interview data is available
  useEffect(() => {
    if (!interviewId)                        return;
    if (!interviewData?.questions?.length)   return;
    if (isErr)                               return;
    if (isPreStartActive)                    return;
    if (isFeedbackOpen)                      return;

    const saved = loadFeedbackState(interviewId);
    if (!saved || saved.completed || !saved.open) return;

    feedbackRestoreInProgressRef.current = true;
    freezeForFinalSubmission();
    setFeedbackStarted(!!saved.started);
    setFeedbackStep(typeof saved.step === "number" ? saved.step : 0);
    setFeedbackAnswers(
      saved.answers && typeof saved.answers === "object" ? saved.answers : {},
    );
    setIsFeedbackOpen(true);
    queueMicrotask(() => { feedbackRestoreInProgressRef.current = false; });
  }, [interviewId, interviewData, isErr, isPreStartActive, isFeedbackOpen, freezeForFinalSubmission]);

  // bfcache safety net
  useEffect(() => {
    const onPageShow = (e) => {
      if (!e.persisted) return;
      _hasFetched = false;
      setInterviewData({});
      setCurrentQuestion(1);
      setIsLive(false);
      setIsTtsCompletedForCurrentQuestion(false);
      setIsErr(null);
      setIsPreStartActive(false);
      setCameraError(null);
      hasPlayedFirstTtsRef.current = false;
      isResumingRef.current        = false;
      _hasFetched = true;
      runFetchInterviewData();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [runFetchInterviewData]);

  // ── per-question attempt tracking ─────────────────────────────────────────

  useEffect(() => {
    if (isPreStartActive)                  return;
    if (!interviewData?.questions?.length) return;
    if (isErr)                             return;
    if (isFeedbackOpen)                    return;
    if (!permissionResolved)               return;
    if (cameraErrorRef.current || cameraError) return;

    const qNum     = currentQuestion;
    const totalQ   = interviewData.questions.length;
    const newCount = incrementAttempt(interviewId, qNum);

    if (newCount === 1) {
      if (permissionReadyRef.current) {
        if (!recordingStartedLoggedByQRef.current.has(qNum)) {
          recordingStartedLoggedByQRef.current.add(qNum);
          addLogWithQuestionText("QUESTION_RECORDING_STARTED", qNum, {
            attemptNumber: 1,
          });
        }
      } else {
        pendingRecordingStartedByQRef.current.add(qNum);
      }
    } else if (newCount === WARN_AT_ATTEMPT) {
      addLogWithQuestionText("QUESTION_RE_RECORD_ATTEMPT", qNum, {
        attemptNumber : newCount,
        maxAllowed    : MAX_ATTEMPTS_PER_QUESTION,
        action        : "first_warning_shown",
      });
      reRecordWarningQNum.current = qNum;
      setShowReRecordWarning(true);
    } else if (newCount === MAX_ATTEMPTS_PER_QUESTION) {
      addLogWithQuestionText("MAX_RE_RECORD_ATTEMPTS_EXCEEDED", qNum, {
        attemptNumber : newCount,
        maxAllowed    : MAX_ATTEMPTS_PER_QUESTION,
        action        : "final_warning_shown_then_auto_advance",
      });
      reRecordWarningQNum.current    = qNum;
      pendingAutoAdvanceRef.current  = () => { autoAdvanceWithUpload(qNum); };
      setShowFinalWarning(true);
    } else if (newCount > MAX_ATTEMPTS_PER_QUESTION) {
      addLogWithQuestionText("MAX_RE_RECORD_ATTEMPTS_EXCEEDED", qNum, {
        attemptNumber : newCount,
        maxAllowed    : MAX_ATTEMPTS_PER_QUESTION,
        action        : "auto_advanced_silently",
      });
      autoAdvanceWithUpload(qNum);
    } else {
      addLogWithQuestionText("QUESTION_RE_RECORD_ATTEMPT", qNum, {
        attemptNumber : newCount,
        maxAllowed    : MAX_ATTEMPTS_PER_QUESTION,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, isPreStartActive, interviewData?.questions?.length, cameraError, permissionResolved]);

  // Pre-start countdown
  useEffect(() => {
    if (!isPreStartActive || isErr || preStartSeconds <= 0) {
      if (isPreStartActive && preStartSeconds <= 0) setIsPreStartActive(false);
      return;
    }
    const id = setInterval(() => {
      setPreStartSeconds((p) => {
        if (p - 1 <= 0) { clearInterval(id); return 0; }
        return p - 1;
      });
    }, 1_000);
    return () => clearInterval(id);
  }, [isPreStartActive, preStartSeconds, isErr]);

  // TTS trigger
  useEffect(() => {
    if (isErr || isPreStartActive)         return;
    if (!interviewData?.questions?.length) return;
    if (isFeedbackOpen)                    return;

    if (isResumingRef.current) {
      isResumingRef.current = false;

      const navEntries = performance.getEntriesByType("navigation");
      const isReload   = navEntries?.[0]?.type === "reload";
      if (isReload) {
        startAnswerTimerRef.current?.();
        setIsTtsCompletedForCurrentQuestion(true);
        return;
      }

      const existingAttempts = loadAttempts(interviewId);
      const attemptCount     = existingAttempts[currentQuestion] ?? 0;
      if (attemptCount >= 2) {
        startAnswerTimerRef.current?.();
        setIsTtsCompletedForCurrentQuestion(true);
      } else {
        const text = interviewData.questions[currentQuestion - 1]?.questionText;
        if (text) {
          fetchAudioScript(text);
        } else {
          startAnswerTimerRef.current?.();
          setIsTtsCompletedForCurrentQuestion(true);
        }
      }
      return;
    }

    if (currentQuestion !== 1 || hasPlayedFirstTtsRef.current) return;
    hasPlayedFirstTtsRef.current = true;

    const existingAttempts = loadAttempts(interviewId);
    const attemptCount     = existingAttempts[currentQuestion] ?? 0;
    if (attemptCount >= 2) {
      startAnswerTimerRef.current?.();
      setIsTtsCompletedForCurrentQuestion(true);
      return;
    }

    const text = interviewData.questions[0]?.questionText;
    if (text) {
      fetchAudioScript(text);
    } else {
      startAnswerTimerRef.current?.();
      setIsTtsCompletedForCurrentQuestion(true);
    }
  }, [isPreStartActive, isErr, interviewData, currentQuestion, fetchAudioScript, isFeedbackOpen, interviewId]);

  // Revoke audio URL on change
  useEffect(() => {
    return () => { if (audioUrl) URL.revokeObjectURL(audioUrl); };
  }, [audioUrl]);

  // Cleanup pre-answer interval on unmount
  useEffect(() => {
    return () => {
      if (preAnswerIntervalRef.current) {
        clearInterval(preAnswerIntervalRef.current);
        preAnswerIntervalRef.current = null;
      }
    };
  }, []);

  // Tab visibility logging
  useEffect(() => {
    const onVisibility = () => {
      if (cameraErrorRef.current) return;
      if (Date.now() < suppressTabLogsUntilRef.current) return;

      const inSubmission =
        !!showCompletionOverlay || !!isCompletionPending || !!isFinalizing;
      const inFeedback  = !!isFeedbackOpenRef.current;
      const qForTab     = inFeedback
        ? 0
        : typeof currentQuestionRef.current === "number"
          ? currentQuestionRef.current
          : currentQuestion;
      const extraForTab = inFeedback ? { questionText: "Feedback" } : {};

      if (document.hidden) {
        if (inSubmission) return;
        if (!tabHiddenAtRef.current) tabHiddenAtRef.current = Date.now();
        if (!tabVisibilityLoggedRef.current) {
          const existingTabSwitches = getLogs(interviewId).filter(
            (l) =>
              l?.event === "TAB_SWITCHED_AWAY" ||
              l?.event_name === "TAB_SWITCHED_AWAY" ||
              l?.eventName === "TAB_SWITCHED_AWAY" ||
              l?.event_Name === "TAB_SWITCHED_AWAY",
          ).length;
          addLogWithQuestionText("TAB_SWITCHED_AWAY", qForTab, {
            count: existingTabSwitches + 1,
            ...extraForTab,
          });
          tabVisibilityLoggedRef.current = true;
        }
      } else {
        if (inSubmission) return;
        const hiddenTime = tabHiddenAtRef.current;
        if (hiddenTime) {
          const existingTabReturns = getLogs(interviewId).filter(
            (l) =>
              l?.event === "TAB_RETURNED" ||
              l?.event_name === "TAB_RETURNED" ||
              l?.eventName === "TAB_RETURNED" ||
              l?.event_Name === "TAB_RETURNED",
          ).length;
          addLogWithQuestionText("TAB_RETURNED", qForTab, {
            count: existingTabReturns + 1,
            ...extraForTab,
          });
        }
        tabHiddenAtRef.current         = null;
        tabVisibilityLoggedRef.current = false;
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [
    interviewId,
    currentQuestion,
    addLogWithQuestionText,
    showCompletionOverlay,
    isCompletionPending,
    isFinalizing,
    isFeedbackOpen,
  ]);

  // Prevent accidental reload / F5
  useEffect(() => {
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Your interview is in progress. Are you sure you want to leave?";
    };
    const onKeyDown = (e) => {
      if (
        e.key === "F5" ||
        (e.ctrlKey && e.key === "r") ||
        (e.metaKey && e.key === "r")
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, []);

  // Back navigation logging
  useEffect(() => {
    if (!interviewId) return;
    const onPopState = () => {
      try {
        const fb       = loadFeedbackState(interviewId);
        const inFeedback =
          !!(isFeedbackOpenRef.current || (fb?.open && !fb?.completed));
        addLogWithQuestionText(
          "BACK_NAVIGATION",
          inFeedback ? 0 : currentQuestionRef.current,
          {
            action: "browser_back_or_history_navigation",
            ...(inFeedback ? { questionText: "Feedback" } : {}),
          },
        );
      } catch (_) {}
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [interviewId, addLogWithQuestionText]);

  // Permission revocation monitor
  useEffect(() => {
    let cameraStatus = null;
    let micStatus    = null;
    let revokeTimer  = null;
    const pendingRevokeRef = { camera: false, mic: false };

    const stopAll = () => {
      timerRef.current?.pause?.();
      setIsLive(false);
      if (preAnswerIntervalRef.current) {
        clearInterval(preAnswerIntervalRef.current);
        preAnswerIntervalRef.current = null;
      }
      setPreAnswerCountdown(0);
      setIsTtsCompletedForCurrentQuestion(false);
      try {
        if (audioPlayerRef.current) {
          audioPlayerRef.current.pause();
          audioPlayerRef.current.src = "";
        }
      } catch (_) {}
      try {
        if (audioRecorderRef.current?.state === "recording") audioRecorderRef.current.stop();
      } catch (_) {}
      try {
        if (mediaRecorderRef.current) mediaRecorderRef.current.stopRecording?.();
      } catch (_) {}
    };

    const flushRevokedLogs = () => {
      revokeTimer = null;
      if (cameraError) return;

      const camRevoked = pendingRevokeRef.camera;
      const micRevoked = pendingRevokeRef.mic;
      pendingRevokeRef.camera = false;
      pendingRevokeRef.mic    = false;

      const existing = loadSession(interviewId) || {};
      const ps       = existing.permissionState || {};

      if (camRevoked && micRevoked) {
        if (ps.bothRevoked) return;
        try {
          saveSession(interviewId, {
            ...existing,
            permissionState: {
              ...ps,
              cameraRevoked: true,
              micRevoked   : true,
              bothRevoked  : true,
            },
          });
        } catch (_) {}
        setCameraError("Camera and microphone access were revoked.");
        stopAll();
        const inFeedback = !!isFeedbackOpenRef.current;
        addLogWithQuestionText(
          "CAMERA_MIC_REVOKED_MID_INTERVIEW",
          inFeedback ? 0 : currentQuestionRef.current,
          {
            device: "camera+microphone",
            ...(inFeedback ? { questionText: "Feedback" } : {}),
          },
        );
        return;
      }
      if (camRevoked) {
        if (ps.cameraRevoked) return;
        try {
          saveSession(interviewId, {
            ...existing,
            permissionState: {
              ...ps,
              cameraRevoked: true,
              bothRevoked  : ps.bothRevoked || false,
            },
          });
        } catch (_) {}
        setCameraError("Camera access was revoked.");
        stopAll();
        const inFeedback = !!isFeedbackOpenRef.current;
        addLogWithQuestionText(
          "CAMERA_REVOKED_MID_INTERVIEW",
          inFeedback ? 0 : currentQuestionRef.current,
          {
            device: "camera",
            ...(inFeedback ? { questionText: "Feedback" } : {}),
          },
        );
        return;
      }
      if (micRevoked) {
        if (ps.micRevoked) return;
        try {
          saveSession(interviewId, {
            ...existing,
            permissionState: {
              ...ps,
              micRevoked : true,
              bothRevoked: ps.bothRevoked || false,
            },
          });
        } catch (_) {}
        setCameraError("Microphone access was revoked.");
        stopAll();
        const inFeedback = !!isFeedbackOpenRef.current;
        addLogWithQuestionText(
          "MIC_REVOKED_MID_INTERVIEW",
          inFeedback ? 0 : currentQuestionRef.current,
          {
            device: "microphone",
            ...(inFeedback ? { questionText: "Feedback" } : {}),
          },
        );
      }
    };

    const scheduleFlushRevokedLogs = () => {
      if (revokeTimer) return;
      revokeTimer = window.setTimeout(flushRevokedLogs, 120);
    };

    const setup = async () => {
      try {
        cameraStatus = await navigator.permissions.query({ name: "camera" });
        micStatus    = await navigator.permissions.query({ name: "microphone" });

        cameraStatus.onchange = () => {
          if (
            cameraStatus.state === "denied" ||
            cameraStatus.state === "prompt"
          ) {
            pendingRevokeRef.camera = true;
            scheduleFlushRevokedLogs();
          }
        };
        micStatus.onchange = () => {
          if (
            micStatus.state === "denied" ||
            micStatus.state === "prompt"
          ) {
            pendingRevokeRef.mic = true;
            scheduleFlushRevokedLogs();
          }
        };
      } catch (e) {
        console.warn("[Interview] Permissions API not supported:", e);
      }
    };

    setup();

    return () => {
      if (cameraStatus) cameraStatus.onchange = null;
      if (micStatus)    micStatus.onchange    = null;
      if (revokeTimer) { clearTimeout(revokeTimer); revokeTimer = null; }
    };
  }, [cameraError, addLogWithQuestionText, interviewId]);

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <>
      <audio ref={audioPlayerRef} autoPlay />

      <InterviewBackdrop open={isFinalizing}            variant="submitting"  />
      <InterviewBackdrop open={showCompletionOverlay}   variant="completion"  />

      <InterviewFeedbackModal
        open={isFeedbackOpen}
        interviewId={interviewId}
        started={feedbackStarted}
        setStarted={setFeedbackStarted}
        step={feedbackStep}
        setStep={setFeedbackStep}
        questions={FEEDBACK_QUESTIONS}
        answers={feedbackAnswers}
        setAnswers={setFeedbackAnswers}
        apiError={null}
        onRetryUpload={null}
        onSubmitAnswer={submitFeedbackAnswer}
        completionPending={isCompletionPending && !completionReadyRef.current}
        onWaitForCompletion={waitForBackgroundCompletion}
        onFinish={handleFeedbackSubmit}
      />

      <AttemptWarningDialog
        open={showReRecordWarning && !cameraError}
        variant="warn"
        questionNumber={reRecordWarningQNum.current}
        isLastQuestion={
          reRecordWarningQNum.current === interviewData?.questions?.length
        }
        onPrimary={() => {
          setShowReRecordWarning(false);
          if (!cameraErrorRef.current) {
            startAnswerTimerRef.current?.();
            setIsTtsCompletedForCurrentQuestion(true);
          }
        }}
      />

      <AttemptWarningDialog
        open={showFinalWarning && !cameraError}
        variant="final"
        questionNumber={reRecordWarningQNum.current}
        isLastQuestion={
          reRecordWarningQNum.current === interviewData?.questions?.length
        }
        onPrimary={handleFinalWarningAcknowledge}
      />

      <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: 4 }}>
        <Container maxWidth="xl">

          {isErr ? (
            <Box
              sx={{
                height        : "70vh",
                display       : "flex",
                alignItems    : "center",
                justifyContent: "center",
                textAlign     : "center",
                px            : 2,
              }}
            >
              <Alert severity="error" sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="h6" color="error.main" sx={{ m: 0 }}>
                  {isErr}
                </Typography>
              </Alert>
            </Box>

          ) : isPreStartActive ? (
            <Box
              sx={{
                height        : "70vh",
                display       : "flex",
                flexDirection : "column",
                alignItems    : "center",
                justifyContent: "center",
                textAlign     : "center",
                gap           : 2,
              }}
            >
              <Typography variant="h4" fontWeight={600}>
                Your interview will start in {preStartSeconds} seconds
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please get ready. Ensure your camera and microphone are enabled.
              </Typography>
            </Box>

          ) : interviewData?.questions ? (
            <Grid container spacing={3}>

              <Grid item size={{ xs: 12, md: 8 }}>
                <VideoPreview
                  isLive={isLive}
                  mediaStreamRef={mediaStreamRef}
                  audioStreamRef={audioStreamRef}
                  onRecordingStart={handleRecordingStart}
                  timeRemaining={timeRemaining}
                  cameraError={cameraError}
                  onCameraError={handleCameraError}
                  onCameraGranted={handleCameraGranted}
                  onMicGranted={handleMicGranted}
                  onBothGranted={handleBothGranted}
                  preAnswerCountdown={preAnswerCountdown}
                  suppressOverlays={!!isFeedbackOpen}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
                <Stack
                  spacing={3}
                  sx={{ flex: 1, alignItems: "stretch", justifyContent: "space-between" }}
                >
                  {cameraError && (
                    <Alert
                      severity="error"
                      action={
                        <Button
                          color="warning"
                          size="small"
                          variant="contained"
                          onClick={() => window.location.reload()}
                          sx={{
                            textTransform: "none",
                            fontWeight   : 600,
                            whiteSpace   : "nowrap",
                          }}
                        >
                          Reload
                        </Button>
                      }
                    >
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.3 }}>
                        Camera or microphone blocked
                      </Typography>
                      <Typography variant="body2">
                        Allow access in your browser's address bar, then reload to continue.
                      </Typography>
                    </Alert>
                  )}

                  <Box
                    sx={{
                      flex         : 1,
                      display      : "flex",
                      flexDirection: "column",
                      minHeight    : 0,
                      position     : "relative",
                      borderRadius : 2,
                      overflow     : "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        ...(cameraError && {
                          filter      : "blur(10px)",
                          userSelect  : "none",
                          pointerEvents: "none",
                        }),
                      }}
                      aria-hidden={cameraError ? "true" : undefined}
                    >
                      {isFeedbackOpen ? (
                        <Paper
                          elevation={0}
                          sx={{
                            p            : 2,
                            border       : "1px solid rgba(170, 168, 160, 0.3)",
                            borderRadius : 2,
                            height       : "100%",
                            display      : "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            textAlign    : "center",
                            bgcolor      : "#fff",
                          }}
                        >
                          <Typography variant="body1" fontWeight={800}>
                            Feedback in progress
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.75 }}
                          >
                            Please complete the feedback form to finish your
                            submission.
                          </Typography>
                        </Paper>
                      ) : (
                        <>
                          <Timer
                            ref={timerRef}
                            durationSeconds={currentQuestionDuration}
                            questionKey={currentQuestion}
                            onTimeUp={handleTimeUp}
                            startPaused={true}
                            onTick={setTimeRemaining}
                          />
                          <CurrentQuestion
                            question={currentQuestionText}
                            questionNumber={currentQuestion}
                            onNext={handleNextQuestion}
                            isSubmit={
                              currentQuestion === interviewData?.questions?.length
                            }
                            isNextEnabled={
                              isTtsCompletedForCurrentQuestion && !cameraError
                            }
                            isLoading={isFinalizing || isFeedbackOpen}
                          />
                        </>
                      )}
                    </Box>

                    {cameraError && (
                      <Box
                        sx={{
                          position      : "absolute",
                          inset         : 0,
                          borderRadius  : 2,
                          display       : "flex",
                          alignItems    : "center",
                          justifyContent: "center",
                          textAlign     : "center",
                          px            : 2,
                          background    : "rgba(255,255,255,0.92)",
                          border        : "1px solid rgba(170, 168, 160, 0.3)",
                          backdropFilter: "blur(2px)",
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="text.secondary"
                        >
                          Enable camera/microphone permissions to view the question.
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Paper
                    elevation={0}
                    sx={{
                      p           : 1.5,
                      border      : "1px solid rgba(170,168,160,0.3)",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="#ef6e13">
                      <strong style={{ fontWeight: 600, fontSize: 16 }}>
                        AI Monitoring Active :
                      </strong>{" "}
                      Your behavior, responses, and engagement are being monitored.
                      Please maintain eye contact with the camera and avoid switching
                      tabs or typing.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={2}>
                      Take your time to provide a thoughtful answer. Speak clearly and
                      look at the camera. Please do not switch tabs or refresh the page
                      while recording.
                    </Typography>
                  </Paper>
                </Stack>
              </Grid>

              <Grid item size={{ xs: 12, md: 8 }}>
                <ProgressTracker
                  currentQuestion={
                    isFeedbackOpen
                      ? (interviewData?.questions?.length || currentQuestion) + 1
                      : currentQuestion
                  }
                  totalQuestions={interviewData?.questions?.length}
                />
              </Grid>

            </Grid>

          ) : (
            <Box
              sx={{
                height        : "70vh",
                display       : "flex",
                alignItems    : "center",
                justifyContent: "center",
                textAlign     : "center",
                px            : 2,
              }}
            >
              <Typography variant="h5">Loading...</Typography>
            </Box>
          )}

        </Container>
      </Box>
    </>
  );
};

export default Interview;